import React, { Fragment } from 'react';
import waybillHeader from 'public/icons/waybill-header.svg';
import _get from 'lodash/get';
import PropTypes from 'prop-types';
import QRCode from 'react-qr-code';
import I18n from 'i18n-js';
import { paymentMethodForDisplay } from 'helper/payment';
import { CASH, CASH_BY_RECIPIENT } from 'constants/booking-constants';
import { formatMajorUnitsForDisplay, getAmountDisplay } from 'helper/order';
import _isEmpty from 'lodash/isEmpty';
import { formatDeliveryTime } from 'helper/waybill-helpers';
import css from './waybill.less';
import { ReferenceCode } from './reference-code';

export const Waybill = ({ order, dropoffStepIndex }) => {
  const isMSD = _get(order, 'steps.length') > 2;
  const senderDetails = _get(order, 'steps[0]', {});
  const recipientDetails = _get(order, `steps[${dropoffStepIndex}]`);
  if (_isEmpty(senderDetails) || _isEmpty(recipientDetails)) {
    return null;
  }
  const { itemInfo } = recipientDetails;
  const { amount } = order;
  const paymentMethod = _get(order, 'paymentMethod', {});
  const currency = _get(order, 'currency', {});
  const serviceName = _get(order, 'service.serviceName', '-');
  const addressInstructions = _get(recipientDetails, 'contact.instruction');
  const deliveryInstructions =
    _get(recipientDetails, 'contact.driverNotes') ||
    _get(order, 'noteToDriver') ||
    '--';
  const { enable: isCOD, amount: CODAmount } = _get(
    recipientDetails,
    'cashOnDelivery',
    {
      enable: false,
    },
  );

  const isCashByRecipient =
    paymentMethod.type === CASH &&
    paymentMethod.name === CASH &&
    paymentMethod.paidBy === CASH_BY_RECIPIENT;

  return (
    <div className={css.container}>
      <div className={css.header}>
        <img src={waybillHeader} alt="" />
        <div className={css.headerRight}>
          {isMSD ? (
            <Fragment>
              <h1>{_get(recipientDetails, 'orderID', '--')}</h1>
              <span className={css.msdStopLabelContainer}>
                <h1>{dropoffStepIndex}</h1>
              </span>
            </Fragment>
          ) : (
            <h1>{_get(order, 'orderID', '--')}</h1>
          )}
        </div>
        <div />
      </div>
      <div className={css.recipientDetailsContainer}>
        <div className={css.recipientDetails}>
          <p className={css.deliverTo}>{`${I18n.t(
            'v2.waybill.deliver_to',
          )}:`}</p>
          <h2 className={css.name}>{recipientDetails.contact.name}</h2>
          <p className={css.address}>{recipientDetails.location.address}</p>
          {addressInstructions && <p> {addressInstructions} </p>}
        </div>
        <div className={css.qrCodeContainer}>
          <QRCode
            value={
              isMSD
                ? _get(recipientDetails, 'orderID', '--')
                : _get(order, 'orderID', '--')
            }
            size={225}
          />
        </div>
      </div>
      <div className={css.itemInfo}>
        <table>
          <tbody>
            {isMSD && (
              <tr>
                <td className={css.itemInfoName}>
                  {I18n.t('v2.waybill.bookingCode')}
                </td>
                <td className={css.itemInfoData}>
                  {_get(order, 'bookingCode', '--')}
                </td>
              </tr>
            )}
            <tr>
              <td className={css.itemInfoName}>
                {I18n.t('v2.waybill.delivery_by_date')}
              </td>
              <td className={css.itemInfoData}>
                {formatDeliveryTime(_get(order, 'createMeta.createAtInTs'))}
              </td>
            </tr>
            <tr>
              <td className={css.itemInfoName}>
                {I18n.t('v2.waybill.item_weight')}
              </td>
              <td className={css.itemInfoData}>
                {`${_get(itemInfo, 'weight', 0)}kg`}
              </td>
            </tr>
            <tr>
              <td className={css.itemInfoName}>
                {I18n.t('v2.waybill.delivery_instructions')}
              </td>
              <td className={css.itemInfoData}>{deliveryInstructions}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className={css.senderDetails}>
        <div>
          <p className={css.senderHeader}>
            {I18n.t('v2.waybill.sender_details')}
          </p>
          <p>{senderDetails.contact.name}</p>
          <p>{senderDetails.location.address}</p>
          <p />
        </div>
        <div>
          <p className={css.senderHeader}>
            {I18n.t('v2.waybill.delivery_type')}
          </p>
          <p>{serviceName}</p>
          <br />
          <p className={css.senderHeader}>{I18n.t('v2.waybill.cod_amount')}</p>
          <p>
            {isCOD ? formatMajorUnitsForDisplay(CODAmount, currency) : '--'}
          </p>
        </div>
        <div>
          <p className={css.senderHeader}>
            {I18n.t('v2.waybill.payment_method')}
          </p>
          <p>
            {paymentMethodForDisplay(
              paymentMethod,
              false,
              currency.code === 'IDR' ? 'ID' : '',
            )}
          </p>
          {isCashByRecipient && (
            <Fragment>
              <p className={css.senderHeader}>
                {I18n.t('v2.waybill.payment_amount')}
              </p>
              <p>{getAmountDisplay(amount, currency, false)}</p>
            </Fragment>
          )}
        </div>
      </div>
      <ReferenceCode refCode={order.referenceCode} />
      <div className={css.thankyou}>{I18n.t('v2.waybill.thank_you')}</div>
    </div>
  );
};

Waybill.propTypes = {
  order: PropTypes.shape({}).isRequired,
  dropoffStepIndex: PropTypes.number,
};

Waybill.defaultProps = {
  dropoffStepIndex: 1,
};

export default {};
